<script setup lang="ts">
import DashboardRequestList from "@/components/requests/DashboardRequestList.vue";
import DashboardTrainLineupCompact from "@/components/trains/DashboardTrainLineupCompact.vue";
import ClipOnList from "@/components/clipons/ClipOnList.vue";
import EmployeeTaskList from "@/components/tasks/EmployeeTaskList.vue";
import ActivityFeedCard from "@/components/activity/ActivityFeedCard.vue";
import backendCall from "@/services/AxiosService";
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useRequestsStore } from "@/stores/requestStore";

// Initialize the requests store
const requestsStore = useRequestsStore();

// Data structure for clips
const clips = ref({
  clipsUnpretripped: [],
  clipsReadyToInstall: [],
  failedPretrip: [],
  clipsNeedFuel: []  // New property for clips requiring fuel
});

// Data structure for powerpacks
const powerpacks = ref({
  needsPretrip: [],
  needsFuel: []
});

// Railway metrics data
const railwayMetrics = ref({
  activeTrains: 0,
  pendingRequests: 0,
  completedYardChecks: 0,
  fuelUsage: 0
});

// Fetch clip-on data
const fetchClips = async () => {
  try {
    const fetchClipsCall = await backendCall.get("/clip-ons");
    clips.value = fetchClipsCall.data.data;

    // Filter clips that need fuel (low fuel levels)
    if (fetchClipsCall.data.data.clipsUnpretripped) {
      clips.value.clipsNeedFuel = fetchClipsCall.data.data.clipsUnpretripped.filter(
        clip => clip.current_fuel_level && 
        (clip.current_fuel_level === 'E' || 
         clip.current_fuel_level === '1/8' || 
         clip.current_fuel_level === '1/4' || 
         clip.current_fuel_level === '3/8')
      );
    }
  } catch (error) {
    console.error("Error fetching clip-ons:", error);
  }
};

// Fetch powerpack data
const fetchPowerpacks = async () => {
  try {
    // Fetch powerpacks that need pretrip
    const pretripResponse = await backendCall.get("/power-pack/inventory/wit");
    if (pretripResponse.data.success) {
      // Filter powerpacks that need pretrip (latest_pretrip_passed is null or false)
      powerpacks.value.needsPretrip = pretripResponse.data.data.filter(
        pack => pack.latest_pretrip_passed === null || pack.latest_pretrip_passed === false
      ).map(pack => ({
        clip_number: pack.genset_number, // Map to same structure as clips for component reuse
        removal_time: pack.in_time,
        current_fuel_level: pack.fuel_level || "Unknown" // Default fuel level
      }));

      // Filter powerpacks that need fuel (low fuel levels)
      powerpacks.value.needsFuel = pretripResponse.data.data.filter(
        pack => pack.fuel_level && 
        (pack.fuel_level === 'E' || 
         pack.fuel_level === '1/8' || 
         pack.fuel_level === '1/4' || 
         pack.fuel_level === '3/8')
      ).map(pack => ({
        clip_number: pack.genset_number,
        removal_time: pack.in_time,
        current_fuel_level: pack.fuel_level
      }));
    }
  } catch (error) {
    console.error("Error fetching powerpacks:", error);
  }
};

// Fetch railway metrics
const fetchRailwayMetrics = async () => {
  try {
    const metricsCall = await backendCall.get("/dashboard/metrics");
    railwayMetrics.value = metricsCall.data.data;
  } catch (error) {
    console.error("Error fetching metrics:", error);
  }
};

// Variables to store interval references
let fetchClipsInterval: ReturnType<typeof setInterval>;
let fetchPowerpacksInterval: ReturnType<typeof setInterval>;
let fetchRailwayMetricsInterval: ReturnType<typeof setInterval>;
let fetchRequestsInterval: ReturnType<typeof setInterval>;

onMounted(async () => {
  // Initial data fetch
  await fetchClips();
  await fetchPowerpacks();
  await fetchRailwayMetrics();
  await requestsStore.fetchRequests();

  // Set up intervals to refresh data every 60 seconds
  fetchClipsInterval = setInterval(fetchClips, 60000);
  fetchPowerpacksInterval = setInterval(fetchPowerpacks, 60000);
  fetchRailwayMetricsInterval = setInterval(fetchRailwayMetrics, 60000);
  fetchRequestsInterval = setInterval(() => requestsStore.fetchRequests(), 60000);
});

onUnmounted(() => {
  // Clean up all intervals
  clearInterval(fetchClipsInterval);
  clearInterval(fetchPowerpacksInterval);
  clearInterval(fetchRailwayMetricsInterval);
  clearInterval(fetchRequestsInterval);
});

</script>

<template>
  <div class="dashboard-container fixed inset-0 h-screen w-screen overflow-hidden m-0 p-0 max-w-screen z-50">
    <div class="w-full">
      <div class="min-h-screen flex relative lg:static bg-surface-50 dark:bg-surface-950 w-full">
        <div class="min-h-screen flex flex-col relative flex-auto w-full">
          <div class="p-4 flex flex-col flex-auto w-full">
            <div class="grid grid-cols-12 gap-2 w-full">
              <div class="col-span-12">
<!--            <div class="grid grid-cols-12 gap-4">-->
<!--              <div class="col-span-12 md:col-span-6 lg:col-span-3">-->
<!--                <div class="p-4 text-center bg-blue-500 rounded-xl">-->
<!--                  <span class="inline-flex justify-center items-center bg-blue-600 rounded-full mb-4 w-12 h-12 py-2">-->
<!--                    <i class="pi pi-train !text-xl leading-none text-white" />-->
<!--                  </span>-->
<!--                  <div class="text-2xl font-medium text-white mb-2">{{ railwayMetrics.activeTrains }}</div>-->
<!--                  <span class="text-blue-100 font-medium">Active Trains</span>-->
<!--                </div>-->
<!--              </div>-->
<!--              <div class="col-span-12 md:col-span-6 lg:col-span-3">-->
<!--                <div class="p-4 text-center bg-purple-500 rounded-xl">-->
<!--                  <span class="inline-flex justify-center items-center bg-purple-600 rounded-full mb-4 w-12 h-12 py-2">-->
<!--                    <i class="pi pi-inbox !text-xl leading-none text-white" />-->
<!--                  </span>-->
<!--                  <div class="text-2xl font-medium text-white mb-2">{{ railwayMetrics.pendingRequests }}</div>-->
<!--                  <span class="text-purple-100 font-medium">Pending Requests</span>-->
<!--                </div>-->
<!--              </div>-->
<!--              <div class="col-span-12 md:col-span-6 lg:col-span-3">-->
<!--                <div class="p-4 text-center bg-indigo-500 rounded-xl">-->
<!--                  <span class="inline-flex justify-center items-center bg-indigo-600 rounded-full mb-4 w-12 h-12 py-2">-->
<!--                    <i class="pi pi-check-square !text-xl leading-none text-white" />-->
<!--                  </span>-->
<!--                  <div class="text-2xl font-medium text-white mb-2">{{ railwayMetrics.completedYardChecks }}</div>-->
<!--                  <span class="text-indigo-100 font-medium">Yard Checks</span>-->
<!--                </div>-->
<!--              </div>-->
<!--              <div class="col-span-12 md:col-span-6 lg:col-span-3">-->
<!--                <div class="p-4 text-center bg-orange-500 rounded-xl">-->
<!--                  <span class="inline-flex justify-center items-center bg-orange-600 rounded-full mb-4 w-12 h-12 py-2">-->
<!--                    <i class="pi pi-bolt !text-xl leading-none text-white" />-->
<!--                  </span>-->
<!--                  <div class="text-2xl font-medium text-white mb-2">{{ railwayMetrics.fuelUsage }}</div>-->
<!--                  <span class="text-orange-100 font-medium">Fuel Usage (L)</span>-->
<!--                </div>-->
<!--              </div>-->
<!--            </div>-->
          </div>

          <!-- 3-column layout starts here -->
              <!-- Column 1: Trains -->
              <div class="col-span-12 lg:col-span-4">
                <div class="shadow bg-surface-0 dark:bg-neutral-900 rounded-border rounded-xl p-3 h-full text-xs">
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-lg font-medium text-surface-900 dark:text-surface-0">Trains</span>
                  </div>
                  <DashboardTrainLineupCompact 
                    :max-trains="15"
                  />
                </div>
              </div>

              <!-- Column 2: Requests -->
              <div class="col-span-12 lg:col-span-4">
                <div class="flex flex-col gap-2">
                  <!-- Requests Section -->
                  <div class="bg-surface-0 dark:bg-neutral-900 shadow rounded-border rounded-xl p-3 text-xs">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-lg font-medium text-surface-900 dark:text-surface-0">Outstanding Requests</span>
                    </div>
                    <DashboardRequestList :show-actions="false"/>
                  </div>

                  <!-- Clips Section -->
                  <div class="bg-surface-0 dark:bg-neutral-900 shadow rounded-border rounded-xl p-3 text-xs mt-2">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-lg font-medium text-surface-900 dark:text-surface-0">Unpretripped Clips</span>
                    </div>
                    <ClipOnList 
                      v-if="clips && clips.clipsUnpretripped" 
                      title="Pretrip Required" 
                      list-type="pretrip-required" 
                      :clips="clips.clipsUnpretripped"
                    />
                  </div>

                  <!-- Clips Needing Fuel Section -->
                  <div class="bg-surface-0 dark:bg-neutral-900 shadow rounded-border rounded-xl p-3 text-xs mt-2">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-lg font-medium text-surface-900 dark:text-surface-0">Clips Needing Fuel</span>
                    </div>
                    <ClipOnList 
                      v-if="clips && clips.clipsNeedFuel && clips.clipsNeedFuel.length > 0" 
                      title="Fuel Required" 
                      list-type="fuel-required" 
                      :clips="clips.clipsNeedFuel"
                    />
                    <div v-else class="text-center p-3 text-surface-600 dark:text-surface-400">
                      No clips requiring fuel
                    </div>
                  </div>

                  <!-- Powerpacks Needing Pretrip Section -->
                  <div class="bg-surface-0 dark:bg-neutral-900 shadow rounded-border rounded-xl p-3 text-xs mt-2">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-lg font-medium text-surface-900 dark:text-surface-0">Powerpacks Needing Pretrip</span>
                    </div>
                    <ClipOnList 
                      v-if="powerpacks && powerpacks.needsPretrip && powerpacks.needsPretrip.length > 0" 
                      title="Pretrip Required" 
                      list-type="pretrip-required" 
                      :clips="powerpacks.needsPretrip"
                    />
                    <div v-else class="text-center p-3 text-surface-600 dark:text-surface-400">
                      No powerpacks requiring pretrip
                    </div>
                  </div>

                  <!-- Powerpacks Needing Fuel Section -->
                  <div class="bg-surface-0 dark:bg-neutral-900 shadow rounded-border rounded-xl p-3 text-xs mt-2">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-lg font-medium text-surface-900 dark:text-surface-0">Powerpacks Needing Fuel</span>
                    </div>
                    <ClipOnList 
                      v-if="powerpacks && powerpacks.needsFuel && powerpacks.needsFuel.length > 0" 
                      title="Refuel Required" 
                      list-type="pretrip-required" 
                      :clips="powerpacks.needsFuel"
                    />
                    <div v-else class="text-center p-3 text-surface-600 dark:text-surface-400">
                      No powerpacks requiring fuel
                    </div>
                  </div>
                </div>
              </div>

              <!-- Column 3: Activity Feed -->
              <div class="col-span-12 lg:col-span-4 w-full">
                <ActivityFeedCard 
                  title="Activity Feed"
                  subtitle="What's happening in the yard"
                  :height="650"
                  :limit="30"
                  :show-all="true"
                />
              </div>
              <!-- End of 3-column layout -->
              <div class="col-span-12">
<!--                <div class="grid grid-cols-12 gap-2">-->
<!--                  <div class="col-span-12 lg:col-span-4">-->
<!--                    <div class="shadow bg-surface-0 dark:bg-surface-900 rounded-border p-3 h-full">-->
<!--                      <div class="flex items-start mb-4">-->
<!--                        <span class="inline-flex justify-center items-center bg-blue-600 rounded-full mr-4 w-10 h-10 py-2">-->
<!--                          <i class="pi pi-calendar !text-2xl leading-none text-white" />-->
<!--                        </span>-->
<!--                        <div class="ml-4">-->
<!--                          <span class="block text-surface-900 dark:text-surface-0 mb-1 text-lg font-medium">On-Time Performance</span>-->
<!--                          <p class="text-surface-600 dark:text-surface-200 mt-0 mb-0">Train Arrival Metrics</p>-->
<!--                        </div>-->
<!--                      </div>-->
<!--                      <ul class="list-none p-0 m-0">-->
<!--                        <li class="mb-2">-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-check-circle mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">On Time</span>-->
<!--                            </span>-->
<!--                            <span class="text-cyan-500 font-medium">78.5%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-1 rounded">-->
<!--                            <div class="bg-cyan-500 h-full w-[78.5%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                        <li class="mb-2">-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-clock mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Delayed</span>-->
<!--                            </span>-->
<!--                            <span class="text-indigo-500 font-medium">15.3%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-1 rounded">-->
<!--                            <div class="bg-indigo-500 h-full rounded w-[15.3%]"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                        <li>-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-exclamation-triangle mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Cancelled</span>-->
<!--                            </span>-->
<!--                            <span class="text-orange-500 font-medium">6.2%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-1 rounded">-->
<!--                            <div class="bg-orange-500 h-full w-[6.2%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                      </ul>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                  <div class="col-span-12 lg:col-span-4">-->
<!--                    <div class="shadow bg-surface-0 dark:bg-surface-900 rounded-border p-3 h-full">-->
<!--                      <div class="flex items-start mb-4">-->
<!--                        <span class="inline-flex justify-center items-center bg-green-600 rounded-full mr-4 w-10 h-10 py-2">-->
<!--                          <i class="pi pi-chart-line !text-2xl leading-none text-white" />-->
<!--                        </span>-->
<!--                        <div class="ml-4">-->
<!--                          <span class="block text-surface-900 dark:text-surface-0 mb-1 text-lg font-medium">Yard Efficiency</span>-->
<!--                          <p class="text-surface-600 dark:text-surface-200 mt-0 mb-0">Yard Check Metrics</p>-->
<!--                        </div>-->
<!--                      </div>-->
<!--                      <ul class="list-none p-0 m-0">-->
<!--                        <li class="mb-2">-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-check-square mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Completed</span>-->
<!--                            </span>-->
<!--                            <span class="text-cyan-500 font-medium">92.4%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-1 rounded">-->
<!--                            <div class="bg-cyan-500 h-full w-[92.4%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                        <li class="mb-2">-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-hourglass mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">In Progress</span>-->
<!--                            </span>-->
<!--                            <span class="text-indigo-500 font-medium">5.8%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-[7px] rounded">-->
<!--                            <div class="bg-indigo-500 h-full w-[5.8%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                        <li>-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-times-circle mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Pending</span>-->
<!--                            </span>-->
<!--                            <span class="text-orange-500 font-medium">1.8%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-[7px] rounded">-->
<!--                            <div class="bg-orange-500 h-full w-[1.8%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                      </ul>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                  <div class="col-span-12 lg:col-span-4">-->
<!--                    <div class="shadow bg-surface-0 dark:bg-surface-900 rounded-border p-3 h-full">-->
<!--                      <div class="flex items-start mb-4">-->
<!--                        <span class="inline-flex justify-center items-center bg-orange-600 rounded-full mr-4 w-10 h-10 py-2">-->
<!--                          <i class="pi pi-bolt !text-2xl leading-none text-white" />-->
<!--                        </span>-->
<!--                        <div class="ml-4">-->
<!--                          <span class="block text-surface-900 dark:text-surface-0 mb-1 text-lg font-medium">Resource Usage</span>-->
<!--                          <p class="text-surface-600 dark:text-surface-200 mt-0 mb-0">Fuel & Equipment</p>-->
<!--                        </div>-->
<!--                      </div>-->
<!--                      <ul class="list-none p-0 m-0">-->
<!--                        <li class="mb-2">-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-cog mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Equipment</span>-->
<!--                            </span>-->
<!--                            <span class="text-cyan-500 font-medium">83.2%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-[7px] rounded">-->
<!--                            <div class="bg-cyan-500 h-full w-[83.2%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                        <li class="mb-2">-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-bolt mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Fuel</span>-->
<!--                            </span>-->
<!--                            <span class="text-indigo-500 font-medium">68.7%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-[7px] rounded">-->
<!--                            <div class="bg-indigo-500 h-full w-[68.7%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                        <li>-->
<!--                          <div class="flex justify-between items-center">-->
<!--                            <span class="text-surface-900 dark:text-surface-0 inline-flex justify-between items-center">-->
<!--                              <i class="pi pi-users mr-2"/>-->
<!--                              <span class="font-medium text-surface-900 dark:text-surface-0">Staff</span>-->
<!--                            </span>-->
<!--                            <span class="text-orange-500 font-medium">91.5%</span>-->
<!--                          </div>-->
<!--                          <div class="bg-surface-200 dark:bg-surface-700 w-full mt-2 h-[7px] rounded">-->
<!--                            <div class="bg-orange-500 h-full w-[91.5%] rounded"/>-->
<!--                          </div>-->
<!--                        </li>-->
<!--                      </ul>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Override any parent container constraints */
:deep(.layout-main-container),
:deep(.h-full.p-0.lg\\:p-6.lg\\:pt-2.flex.flex-col),
:deep(.flex-1.border-surface.rounded-border),
:deep(.h-full.w-full.transition-all.duration-300.overflow-hidden),
:deep(.flex-1.overflow-hidden),
:deep(.h-full.w-screen.bg-surface-50.dark\\:bg-surface-950.relative.flex.overflow-hidden),
:deep(.flex.flex-col.h-full.w-full) {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100vw !important;
  width: 100vw !important;
  border-radius: 0 !important;
  overflow: visible !important;
}


/* Ensure EmployeeTaskList takes full width */
:deep(.p-datatable) {
  width: 100% !important;
  max-width: 100% !important;
}

:deep(.p-datatable-wrapper) {
  width: 100% !important;
}

:deep(.p-datatable-table) {
  width: 100% !important;
}

</style>
